import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  LogOut,
  ShieldCheck,
  Search,
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Truck,
  Bike,
  Car,
  Anchor,
  Calendar,
  Hash,
  Loader2,
  AlertCircle,
  ExternalLink,
  XCircle,
  Star,
  Clock,
  Inbox,
  Download,
  Plus,
  Edit3,
  Trash2,
  Save,
  Tv,
  CheckCircle2,
  AlertTriangle,
  MailX,
  MailCheck,
  Heart,
  Zap,
  Flame,
  Bookmark,
  BookmarkCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "amv_admin_token";

const LOGO =
  "https://customer-assets.emergentagent.com/job_98369023-526e-447a-9536-c9b52a5bb5cf/artifacts/mnyeefvz_99DD8AA5-D993-42EB-AA6D-F95FD03FDFF8.png";

const CAT_ICONS = {
  trucking: Truck,
  biking: Bike,
  cars: Car,
  boating: Anchor,
};

const STATUS_META = {
  new: { label: "NEW", cls: "border-[#00E5FF]/50 text-[#00E5FF]", Icon: Inbox },
  reviewed: {
    label: "REVIEWED",
    cls: "border-white/30 text-white/80",
    Icon: Clock,
  },
  shortlisted: {
    label: "SHORTLISTED",
    cls: "border-[#FF2D9C]/60 text-[#FF2D9C]",
    Icon: Star,
  },
  rejected: {
    label: "REJECTED",
    cls: "border-red-500/50 text-red-400",
    Icon: XCircle,
  },
};

// "The Set Speaks" — documentary production tracking workflow.
// Uses its own labels + colors so casting + doc producers don't share semantics.
const DOC_STATUS_META = {
  new: {
    label: "NEW",
    cls: "border-blue-400/60 text-blue-300",
    dot: "bg-blue-400",
    Icon: Inbox,
  },
  reviewing: {
    label: "REVIEWING",
    cls: "border-orange-400/60 text-orange-300",
    dot: "bg-orange-400",
    Icon: Clock,
  },
  contacted: {
    label: "CONTACTED",
    cls: "border-purple-400/60 text-purple-300",
    dot: "bg-purple-400",
    Icon: Mail,
  },
  selected: {
    label: "SELECTED",
    cls: "border-emerald-400/60 text-emerald-300",
    dot: "bg-emerald-400",
    Icon: Star,
  },
  rejected: {
    label: "REJECTED",
    cls: "border-red-500/60 text-red-400",
    dot: "bg-red-500",
    Icon: XCircle,
  },
};

const DOC_STATUS_OPTIONS = ["new", "reviewing", "contacted", "selected", "rejected"];

function isDocKind(kind) {
  return kind === "set-speaks";
}

function statusMetaFor(kind, status) {
  const s = (status || "new").toLowerCase();
  if (isDocKind(kind)) return DOC_STATUS_META[s] || DOC_STATUS_META.new;
  return STATUS_META[s] || STATUS_META.new;
}

function statusOptionsFor(kind) {
  return isDocKind(kind) ? DOC_STATUS_OPTIONS : INTAKE_STATUS_OPTIONS;
}

// ---------- helpers ----------
function authClient(token) {
  return axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

async function downloadCsv(token, statusFilter) {
  try {
    const res = await axios.get(`${API}/admin/export/applications.csv`, {
      headers: { Authorization: `Bearer ${token}` },
      params: statusFilter ? { status: statusFilter } : {},
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.download = `automotoville-applications-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    alert("Failed to download CSV.");
  }
}

// ---------- main ----------
export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState("submissions"); // submissions | shows | intakes

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setSelectedId(null);
  };

  // verify token on mount
  useEffect(() => {
    if (!token) return;
    authClient(token)
      .get("/admin/me")
      .catch(() => handleLogout());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <AdminLogin
        onAuthed={(t) => {
          localStorage.setItem(TOKEN_KEY, t);
          setToken(t);
        }}
      />
    );
  }

  return (
    <div
      className="relative min-h-screen bg-[#05050a] text-white"
      data-testid="admin-shell"
    >
      <AdminHeader
        onLogout={handleLogout}
        tab={tab}
        onTabChange={(t) => {
          setTab(t);
          setSelectedId(null);
        }}
        showTabs={!selectedId}
      />
      {tab === "submissions" ? (
        selectedId ? (
          <AdminDetail
            token={token}
            appId={selectedId}
            onBack={() => setSelectedId(null)}
          />
        ) : (
          <AdminList token={token} onSelect={setSelectedId} />
        )
      ) : tab === "shows" ? (
        <ShowsAdmin token={token} />
      ) : (
        <IntakesAdmin token={token} />
      )}
    </div>
  );
}

// ---------- header ----------
function AdminHeader({ onLogout, tab, onTabChange, showTabs }) {
  const tabs = [
    { key: "submissions", label: "Submissions" },
    { key: "intakes", label: "Intakes" },
    { key: "shows", label: "Shows" },
  ];
  return (
    <header
      data-testid="admin-header"
      className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3 gap-4">
        <div className="flex items-center gap-3 shrink-0">
          <img
            src={LOGO}
            alt="AutoMotoVille"
            style={{ height: "44px", width: "auto" }}
            className="amv-logo"
          />
          <div className="hidden sm:flex items-center gap-2 px-3 py-1 border border-[#FF2D9C]/40 bg-[#FF2D9C]/10">
            <ShieldCheck className="w-3.5 h-3.5 text-[#FF2D9C]" />
            <span className="font-accent text-[10px] tracking-[0.22em] text-[#FF2D9C]">
              ADMIN
            </span>
          </div>
        </div>

        {showTabs && (
          <nav className="flex items-center gap-1" data-testid="admin-tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => onTabChange(t.key)}
                data-testid={`tab-${t.key}`}
                className={`px-4 py-2 font-accent text-[11px] tracking-[0.22em] border transition-colors ${
                  tab === t.key
                    ? "border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/5"
                    : "border-white/10 text-white/55 hover:text-white hover:border-white/30"
                }`}
              >
                {t.label.toUpperCase()}
              </button>
            ))}
          </nav>
        )}

        <button
          onClick={onLogout}
          data-testid="admin-logout-btn"
          className="flex items-center gap-2 font-accent text-xs tracking-[0.22em] text-white/60 hover:text-white border border-white/10 hover:border-white/30 px-4 py-2 transition-colors shrink-0"
        >
          <LogOut className="w-3.5 h-3.5" /> SIGN OUT
        </button>
      </div>
    </header>
  );
}

// ---------- login ----------
function AdminLogin({ onAuthed }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pw });
      onAuthed(res.data.token);
    } catch (e) {
      setErr(
        e?.response?.status === 401
          ? "Wrong password."
          : "Sign in failed. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#05050a] text-white flex items-center justify-center px-6"
      data-testid="admin-login"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background:
            "radial-gradient(70% 60% at 30% 20%, rgba(255,45,156,0.20), transparent 70%), radial-gradient(60% 50% at 70% 80%, rgba(0,229,255,0.18), transparent 70%)",
        }}
      />
      <motion.form
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        onSubmit={submit}
        className="relative w-full max-w-md bg-[#0d0d14] border border-white/10 p-8"
      >
        <img
          src={LOGO}
          alt="AutoMotoVille"
          style={{ height: "60px", width: "auto" }}
          className="amv-logo mx-auto mb-6"
        />
        <div className="text-center mb-7">
          <div className="font-accent text-xs text-[#FF2D9C] mb-1 tracking-[0.22em]">
            ADMIN ACCESS
          </div>
          <h1 className="font-heading text-2xl font-black">
            Casting Dashboard
          </h1>
        </div>

        <label className="block font-accent text-[11px] tracking-[0.22em] text-white/60 mb-2">
          ADMIN PASSWORD
        </label>
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          autoFocus
          required
          data-testid="admin-password-input"
          placeholder="••••••••••"
          className="w-full bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/40 px-4 py-3 text-white placeholder-white/30 outline-none transition-colors"
        />

        {err && (
          <div
            data-testid="admin-login-error"
            className="mt-4 flex items-start gap-2 text-sm text-[#FF2D9C] border border-[#FF2D9C]/40 bg-[#FF2D9C]/10 px-3 py-2"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{err}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !pw}
          data-testid="admin-login-btn"
          className="mt-6 w-full bg-[#FF2D9C] text-white font-black py-3 uppercase tracking-[0.22em] text-sm glow-pink transition-all hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
            </>
          ) : (
            <>
              <ShieldCheck className="w-4 h-4" /> Enter Dashboard
            </>
          )}
        </button>

        <p className="text-[11px] text-white/40 text-center mt-5">
          Single-admin access. Need a password? Contact the network owner.
        </p>
      </motion.form>
    </div>
  );
}

// ---------- list ----------
function AdminList({ token, onSelect }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await authClient(token).get("/admin/applications", { params });
      setRows(res.data || []);
    } catch (e) {
      setErr("Could not load applications.");
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [
      r.full_name,
      r.email,
      r.city,
      r.state,
      r.vehicle,
      (r.categories || []).join(" "),
    ]
      .join(" ")
      .toLowerCase()
      .includes(q);
  });

  const counts = rows.reduce((acc, r) => {
    acc[r.status || "new"] = (acc[r.status || "new"] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-list">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <div className="font-accent text-xs text-[#00E5FF] mb-2 tracking-[0.22em]">
            CASTING / SUBMISSIONS
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-black leading-none">
            Inbox{" "}
            <span className="text-white/40">({rows.length})</span>
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, city, vehicle…"
              data-testid="admin-search-input"
              className="bg-black/40 border border-white/10 focus:border-[#00E5FF] pl-9 pr-3 py-2 text-sm text-white placeholder-white/40 outline-none w-64"
            />
          </div>
          <button
            onClick={() => downloadCsv(token, statusFilter)}
            data-testid="admin-csv-export-btn"
            className="flex items-center gap-2 font-accent text-xs tracking-[0.22em] text-[#00E5FF] border border-[#00E5FF]/40 hover:bg-[#00E5FF]/10 hover:border-[#00E5FF] px-4 py-2 transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[
          { key: "", label: "ALL", count: rows.length },
          { key: "new", label: "NEW", count: counts.new || 0 },
          {
            key: "reviewed",
            label: "REVIEWED",
            count: counts.reviewed || 0,
          },
          {
            key: "shortlisted",
            label: "SHORTLISTED",
            count: counts.shortlisted || 0,
          },
          {
            key: "rejected",
            label: "REJECTED",
            count: counts.rejected || 0,
          },
        ].map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setStatusFilter(f.key)}
            data-testid={`status-filter-${f.key || "all"}`}
            className={`px-3 py-1.5 font-accent text-[11px] tracking-[0.22em] border transition-colors ${
              statusFilter === f.key
                ? "border-[#FF2D9C] bg-[#FF2D9C]/10 text-[#FF2D9C]"
                : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
            }`}
          >
            {f.label} <span className="opacity-60">({f.count})</span>
          </button>
        ))}
      </div>

      {err && (
        <div className="mb-4 flex items-center gap-2 text-sm text-[#FF2D9C] border border-[#FF2D9C]/40 bg-[#FF2D9C]/10 px-3 py-2">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      {loading ? (
        <div className="py-20 flex items-center justify-center text-white/40">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-white/40 border border-dashed border-white/10">
          No submissions match your filters.
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5">
          {filtered.map((r) => (
            <button
              key={r.id}
              onClick={() => onSelect(r.id)}
              data-testid={`app-row-${r.id}`}
              className="w-full text-left p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4 hover:bg-white/[0.03] transition-colors group"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-heading text-lg md:text-xl font-black truncate">
                    {r.full_name}
                  </div>
                  <StatusBadge status={r.status || "new"} />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-white/55">
                  <span className="flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {r.email}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {r.city}, {r.state}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {fmtDate(r.created_at)}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                {(r.categories || []).map((c) => {
                  const I = CAT_ICONS[c] || Star;
                  return (
                    <span
                      key={c}
                      className="inline-flex items-center gap-1 px-2 py-1 border border-white/15 text-[10px] tracking-widest font-accent text-white/80"
                    >
                      <I className="w-3 h-3 text-[#00E5FF]" /> {c.toUpperCase()}
                    </span>
                  );
                })}
                <EmailStatusBadge rollup={rollUpEmailStatus(r.email_status)} compact />
              </div>

              <div className="text-right shrink-0 hidden md:flex flex-col items-end gap-1">
                <span className="text-[11px] font-accent tracking-widest text-white/40">
                  {r.photos_count || 0} PHOTOS ·{" "}
                  {r.video_file_id ? "VIDEO FILE" : r.video_url ? "VIDEO LINK" : "—"}
                </span>
                <span className="font-accent text-[11px] text-[#00E5FF] tracking-widest group-hover:translate-x-1 transition-transform">
                  OPEN →
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  const Icon = meta.Icon;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-1 border ${meta.cls} text-[10px] font-accent tracking-[0.22em]`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

// ---------- email status ----------
function rollUpEmailStatus(es) {
  // returns: { overall, applicant, admin }
  // overall: 'delivered' (both) | 'partial' (one delivered one not) | 'error' | 'skipped' | 'pending' (no field)
  if (!es || (!es.applicant && !es.admin)) return { overall: "pending" };
  const a = es.applicant?.status;
  const b = es.admin?.status;
  const both = [a, b].filter(Boolean);
  if (both.every((s) => s === "delivered")) return { overall: "delivered", ...es };
  if (both.some((s) => s === "delivered")) return { overall: "partial", ...es };
  if (both.some((s) => s === "error")) return { overall: "error", ...es };
  return { overall: "skipped", ...es };
}

const EMAIL_BADGE = {
  delivered: { label: "EMAILS SENT", cls: "border-emerald-400/50 text-emerald-400", Icon: MailCheck },
  partial: { label: "EMAIL PARTIAL", cls: "border-amber-400/50 text-amber-400", Icon: AlertTriangle },
  error: { label: "EMAIL ERROR", cls: "border-red-500/50 text-red-400", Icon: MailX },
  skipped: { label: "EMAIL SANDBOX", cls: "border-amber-300/40 text-amber-300/90", Icon: Mail },
  pending: { label: "EMAIL PENDING", cls: "border-white/15 text-white/40", Icon: Clock },
};

function EmailStatusBadge({ rollup, compact = false }) {
  const meta = EMAIL_BADGE[rollup.overall] || EMAIL_BADGE.pending;
  const Icon = meta.Icon;
  return (
    <span
      data-testid={`email-badge-${rollup.overall}`}
      title={emailTooltip(rollup)}
      className={`inline-flex items-center gap-1 px-2 py-1 border ${meta.cls} ${
        compact ? "text-[9px]" : "text-[10px]"
      } font-accent tracking-[0.22em]`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

function emailTooltip(rollup) {
  if (rollup.overall === "pending") return "Email status pending — try refreshing in a moment.";
  const lines = [];
  if (rollup.applicant)
    lines.push(`Applicant (${rollup.applicant.to || "—"}): ${rollup.applicant.status}`);
  if (rollup.admin)
    lines.push(`Admin (${rollup.admin.to || "—"}): ${rollup.admin.status}`);
  return lines.join("\n");
}

function EmailStatusPanel({ es }) {
  const rollup = rollUpEmailStatus(es);
  const rows = [
    { key: "applicant", label: "Applicant", data: es?.applicant },
    { key: "admin", label: "Admin", data: es?.admin },
  ];
  const dotCls = (s) => {
    if (s === "delivered") return "bg-emerald-400";
    if (s === "error") return "bg-red-400";
    if (s === "skipped") return "bg-white/40";
    return "bg-white/20";
  };
  return (
    <div
      className="border border-white/10 bg-white/[0.02] p-5"
      data-testid="email-status-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="font-accent text-xs text-[#FF2D9C] tracking-[0.22em]">
          EMAIL DELIVERY
        </div>
        <EmailStatusBadge rollup={rollup} />
      </div>
      {!es ? (
        <p className="text-white/45 text-sm">
          Pending. Emails are dispatched in the background — refresh in a few
          seconds if this just submitted.
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map(({ key, label, data }) => (
            <div
              key={key}
              data-testid={`email-row-${key}`}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-2 border-b border-white/5 last:border-b-0"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className={`w-2 h-2 rounded-full ${dotCls(data?.status)}`} />
                <div className="min-w-0">
                  <div className="font-accent text-[10px] tracking-[0.22em] text-white/55">
                    {label.toUpperCase()}
                  </div>
                  <div className="text-sm text-white/85 truncate max-w-[420px]">
                    {data?.to || "—"}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-accent text-[10px] tracking-[0.22em] text-white/70 uppercase">
                  {data?.status || "pending"}
                </div>
                {data?.id && (
                  <div className="font-accent text-[10px] text-white/30">
                    id · {data.id.slice(0, 8)}
                  </div>
                )}
                {data?.error && (
                  <div className="text-[11px] text-red-400/90 mt-1 max-w-[420px] truncate" title={data.error}>
                    {data.error}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- detail ----------
function AdminDetail({ token, appId, onBack }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [savingStatus, setSavingStatus] = useState("");
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await authClient(token).get(`/admin/applications/${appId}`);
      setDoc(res.data);
    } catch (e) {
      setErr("Could not load this application.");
    } finally {
      setLoading(false);
    }
  }, [token, appId]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (status) => {
    setSavingStatus(status);
    try {
      await authClient(token).patch(`/admin/applications/${appId}`, { status });
      setDoc((d) => (d ? { ...d, status } : d));
    } catch (e) {
      setErr("Failed to update status.");
    } finally {
      setSavingStatus("");
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-20 flex items-center justify-center text-white/40">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (err || !doc) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12">
        <button
          onClick={onBack}
          className="mb-4 flex items-center gap-2 text-white/70 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="border border-[#FF2D9C]/40 bg-[#FF2D9C]/10 text-[#FF2D9C] px-4 py-3 text-sm">
          {err || "Not found."}
        </div>
      </div>
    );
  }

  const ref = (doc.id || "").slice(0, 8).toUpperCase();
  const videoSrc = doc.video_file_id
    ? `${API}/applications/video/${doc.video_file_id}`
    : null;

  return (
    <div
      className="max-w-7xl mx-auto px-6 py-8 md:py-10"
      data-testid="admin-detail"
    >
      <button
        onClick={onBack}
        data-testid="admin-back-btn"
        className="mb-6 flex items-center gap-2 text-white/60 hover:text-white font-accent text-xs tracking-[0.22em]"
      >
        <ArrowLeft className="w-4 h-4" /> BACK TO INBOX
      </button>

      {/* Header card */}
      <div className="border border-white/10 bg-white/[0.02] p-6 md:p-8 mb-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap mb-2">
              <StatusBadge status={doc.status || "new"} />
              <span className="font-accent text-[11px] text-white/40 tracking-[0.22em] flex items-center gap-1">
                <Hash className="w-3 h-3" /> {ref}
              </span>
              <span className="font-accent text-[11px] text-white/40 tracking-[0.22em] flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {fmtDate(doc.created_at)}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-black leading-none">
              {doc.full_name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/70">
              <a
                href={`mailto:${doc.email}`}
                className="inline-flex items-center gap-1 hover:text-[#00E5FF]"
              >
                <Mail className="w-4 h-4" /> {doc.email}
              </a>
              <a
                href={`tel:${doc.phone}`}
                className="inline-flex items-center gap-1 hover:text-[#00E5FF]"
              >
                <Phone className="w-4 h-4" /> {doc.phone}
              </a>
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-4 h-4" /> {doc.city}, {doc.state} · age{" "}
                {doc.age}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {["new", "reviewed", "shortlisted", "rejected"].map((s) => {
              const meta = STATUS_META[s];
              const Icon = meta.Icon;
              const active = (doc.status || "new") === s;
              return (
                <button
                  key={s}
                  onClick={() => updateStatus(s)}
                  disabled={savingStatus === s || active}
                  data-testid={`status-set-${s}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 font-accent text-[11px] tracking-[0.22em] border transition-colors ${
                    active
                      ? `${meta.cls} bg-white/5 cursor-default`
                      : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
                  } disabled:opacity-60`}
                >
                  {savingStatus === s ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Icon className="w-3 h-3" />
                  )}
                  {meta.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left col */}
        <div className="lg:col-span-7 space-y-6">
          {/* Story */}
          <div className="border border-white/10 bg-[#00E5FF]/5 p-6">
            <div className="font-accent text-xs text-[#00E5FF] mb-3 tracking-[0.22em]">
              STORY
            </div>
            <p className="text-white/85 leading-relaxed text-[15px] italic">
              &ldquo;{doc.story}&rdquo;
            </p>
          </div>

          {/* Video */}
          <div className="border border-white/10 bg-white/[0.02] overflow-hidden">
            <div className="px-6 pt-5 pb-3 font-accent text-xs text-[#FF2D9C] tracking-[0.22em]">
              INTRO VIDEO
            </div>
            {videoSrc ? (
              <video
                src={videoSrc}
                controls
                playsInline
                preload="metadata"
                data-testid="admin-video-player"
                className="w-full bg-black aspect-video"
              />
            ) : doc.video_url ? (
              <div className="px-6 py-5">
                <a
                  href={doc.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="admin-video-link"
                  className="inline-flex items-center gap-2 text-[#00E5FF] hover:underline break-all"
                >
                  <ExternalLink className="w-4 h-4 shrink-0" />
                  {doc.video_url}
                </a>
                <p className="text-[12px] text-white/40 mt-2">
                  External link — opens in a new tab.
                </p>
              </div>
            ) : (
              <div className="px-6 py-8 text-white/40 text-sm">No video.</div>
            )}
          </div>

          {/* Photos */}
          <div className="border border-white/10 bg-white/[0.02] p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-accent text-xs text-[#FF2D9C] tracking-[0.22em]">
                PHOTOS ({(doc.photos || []).length})
              </div>
              <span className="font-accent text-[10px] text-white/40 tracking-widest">
                CLICK TO ENLARGE
              </span>
            </div>
            {(doc.photos || []).length === 0 ? (
              <div className="text-white/40 text-sm">No photos.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {doc.photos.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => setLightbox(p.data)}
                    data-testid={`admin-photo-${i}`}
                    className="aspect-square overflow-hidden border border-white/10 hover:border-[#00E5FF]/60 transition-colors group"
                  >
                    <img
                      src={p.data}
                      alt={p.name || `photo-${i + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right col */}
        <div className="lg:col-span-5 space-y-6">
          <div className="border border-white/10 bg-white/[0.02] p-6">
            <div className="font-accent text-xs text-[#00E5FF] mb-4 tracking-[0.22em]">
              LANES
            </div>
            <div className="flex flex-wrap gap-2">
              {(doc.categories || []).map((c) => {
                const I = CAT_ICONS[c] || Star;
                return (
                  <span
                    key={c}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-[#FF2D9C]/40 bg-[#FF2D9C]/5 text-[12px] font-accent tracking-widest"
                  >
                    <I className="w-4 h-4 text-[#FF2D9C]" /> {c.toUpperCase()}
                  </span>
                );
              })}
            </div>
          </div>

          <KV
            rows={[
              ["Years in culture", `${doc.years_in_culture}`],
              ["Vehicle / rig", doc.vehicle],
              ["Social", doc.social_handle || "—"],
            ]}
          />

          <EmailStatusPanel es={doc.email_status} />

          <KV
            rows={[
              ["Email", doc.email],
              ["Phone", doc.phone],
              ["Location", `${doc.city}, ${doc.state}`],
              ["Age", `${doc.age}`],
              ["Reference", ref],
              ["Submitted", fmtDate(doc.created_at)],
            ]}
          />
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          data-testid="photo-lightbox"
          className="fixed inset-0 z-[120] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
        >
          <img
            src={lightbox}
            alt="enlarged"
            className="max-w-[95vw] max-h-[92vh] object-contain border border-white/10"
          />
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-black/60 border border-white/10 hover:bg-[#FF2D9C] flex items-center justify-center"
            aria-label="close"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}

function KV({ rows }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] p-1">
      {rows.map(([k, v]) => (
        <div
          key={k}
          className="flex items-start justify-between gap-3 px-5 py-2.5 border-b border-white/5 last:border-b-0 text-sm"
        >
          <span className="font-accent text-[10px] tracking-[0.22em] text-white/45 pt-1">
            {k.toUpperCase()}
          </span>
          <span className="text-right text-white/85 break-words max-w-[68%]">
            {v || <span className="text-white/30">—</span>}
          </span>
        </div>
      ))}
    </div>
  );
}


// ====================================================================
// SHOWS CMS
// ====================================================================
const SHOW_STATUSES = [
  { key: "now_casting", label: "Now Casting", color: "FF2D9C" },
  { key: "in_development", label: "In Development", color: "00E5FF" },
  { key: "streaming", label: "Streaming", color: "ffffff" },
  { key: "archived", label: "Archived", color: "666666" },
];

const SHOW_TAGS = [
  "unscripted",
  "scripted",
  "docuseries",
  "competition",
  "feature",
];

const EMPTY_SHOW = {
  title: "",
  tag: "unscripted",
  status: "now_casting",
  description: "",
  image_url: "",
  order: 0,
};

function ShowsAdmin({ token }) {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null); // null | EMPTY_SHOW | show object
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await authClient(token).get("/admin/shows");
      setShows(res.data || []);
    } catch (e) {
      setErr("Could not load shows.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const startCreate = () => setEditing({ ...EMPTY_SHOW });
  const startEdit = (s) => setEditing({ ...s });
  const cancelEdit = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    if (!editing.title.trim() || !editing.description.trim()) {
      alert("Title and description are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        // PATCH update
        const { id, created_at, updated_at, ...patch } = editing;
        const res = await authClient(token).patch(
          `/admin/shows/${id}`,
          patch,
        );
        setShows((arr) =>
          arr.map((s) => (s.id === id ? res.data : s)),
        );
      } else {
        const res = await authClient(token).post("/admin/shows", {
          title: editing.title,
          tag: editing.tag,
          status: editing.status,
          description: editing.description,
          image_url: editing.image_url,
          order: Number(editing.order) || 0,
        });
        setShows((arr) => [...arr, res.data]);
      }
      setEditing(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert(typeof d === "string" ? d : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    try {
      await authClient(token).delete(`/admin/shows/${s.id}`);
      setShows((arr) => arr.filter((x) => x.id !== s.id));
    } catch (e) {
      alert("Delete failed.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="shows-admin">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <div className="font-accent text-xs text-[#FF2D9C] mb-2 tracking-[0.22em]">
            CONTENT / SHOWS
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-black leading-none">
            On the Slate{" "}
            <span className="text-white/40">({shows.length})</span>
          </h2>
          <p className="text-white/55 text-sm mt-2 max-w-xl">
            Shows shown on the public landing page. Status drives the badge
            color; archived shows are hidden from the public site.
          </p>
        </div>
        <button
          onClick={startCreate}
          data-testid="show-add-btn"
          className="flex items-center gap-2 bg-[#FF2D9C] text-white font-black px-5 py-3 uppercase tracking-[0.22em] text-xs glow-pink transition-all hover:scale-[1.02]"
        >
          <Plus className="w-4 h-4" /> New Show
        </button>
      </div>

      {err && (
        <div className="mb-4 flex items-center gap-2 text-sm text-[#FF2D9C] border border-[#FF2D9C]/40 bg-[#FF2D9C]/10 px-3 py-2">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      {loading ? (
        <div className="py-20 flex items-center justify-center text-white/40">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : shows.length === 0 ? (
        <div className="py-20 text-center text-white/50 border border-dashed border-white/10">
          <Tv className="w-8 h-8 mx-auto mb-3 text-white/30" />
          No shows yet. Click <strong>New Show</strong> to create the first.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shows.map((s) => (
            <ShowRow
              key={s.id}
              show={s}
              onEdit={() => startEdit(s)}
              onDelete={() => remove(s)}
            />
          ))}
        </div>
      )}

      {editing && (
        <ShowEditor
          show={editing}
          onChange={setEditing}
          onSave={save}
          onCancel={cancelEdit}
          saving={saving}
        />
      )}
    </div>
  );
}

function ShowRow({ show, onEdit, onDelete }) {
  const meta = SHOW_STATUSES.find((s) => s.key === show.status) || SHOW_STATUSES[0];
  return (
    <div
      data-testid={`show-row-${show.id}`}
      className="border border-white/10 bg-white/[0.02] p-5 flex flex-col gap-3 hover:border-white/30 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span
              className="px-2 py-1 border font-accent text-[10px] tracking-[0.22em]"
              style={{
                color: `#${meta.color}`,
                borderColor: `#${meta.color}80`,
              }}
            >
              {meta.label.toUpperCase()}
            </span>
            <span className="font-accent text-[10px] text-white/40 tracking-widest uppercase">
              {show.tag}
            </span>
            <span className="font-accent text-[10px] text-white/30 tracking-widest">
              · ORDER {show.order}
            </span>
          </div>
          <h3 className="font-heading text-2xl font-black leading-tight truncate">
            {show.title}
          </h3>
          <p className="text-white/65 text-sm mt-2 leading-relaxed line-clamp-3">
            {show.description}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/5">
        <span className="text-[10px] font-accent text-white/35 tracking-widest">
          UPDATED {fmtDate(show.updated_at)}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            data-testid={`show-edit-${show.id}`}
            className="flex items-center gap-1 font-accent text-[10px] tracking-[0.22em] text-[#00E5FF] hover:bg-[#00E5FF]/10 border border-[#00E5FF]/40 px-3 py-1.5"
          >
            <Edit3 className="w-3 h-3" /> EDIT
          </button>
          <button
            onClick={onDelete}
            data-testid={`show-delete-${show.id}`}
            className="flex items-center gap-1 font-accent text-[10px] tracking-[0.22em] text-red-400 hover:bg-red-500/10 border border-red-500/40 px-3 py-1.5"
          >
            <Trash2 className="w-3 h-3" /> DELETE
          </button>
        </div>
      </div>
    </div>
  );
}

function ShowEditor({ show, onChange, onSave, onCancel, saving }) {
  const isNew = !show.id;
  const set = (patch) => onChange({ ...show, ...patch });
  return (
    <div
      onClick={onCancel}
      data-testid="show-editor-overlay"
      className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-md flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-6"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl bg-[#0d0d14] border border-white/10"
      >
        <button
          onClick={onCancel}
          data-testid="show-editor-close"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-black/50 hover:bg-[#FF2D9C] hover:text-white text-white/70 border border-white/10"
          aria-label="Close"
        >
          <XCircle className="w-4 h-4" />
        </button>
        <div className="px-6 sm:px-8 py-6 border-b border-white/10">
          <div className="font-accent text-xs text-[#FF2D9C] mb-1 tracking-[0.22em]">
            {isNew ? "NEW SHOW" : "EDIT SHOW"}
          </div>
          <h3 className="font-heading text-2xl font-black">
            {show.title || "Untitled"}
          </h3>
        </div>

        <div className="px-6 sm:px-8 py-6 space-y-5">
          <Field2 label="TITLE *">
            <input
              data-testid="show-title-input"
              className={fieldCls}
              value={show.title}
              onChange={(e) => set({ title: e.target.value })}
              maxLength={120}
              placeholder="Midnight Diesel"
            />
          </Field2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field2 label="STATUS">
              <select
                data-testid="show-status-select"
                className={fieldCls}
                value={show.status}
                onChange={(e) => set({ status: e.target.value })}
              >
                {SHOW_STATUSES.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field2>
            <Field2 label="TAG">
              <select
                data-testid="show-tag-select"
                className={fieldCls}
                value={show.tag}
                onChange={(e) => set({ tag: e.target.value })}
              >
                {SHOW_TAGS.map((t) => (
                  <option key={t} value={t}>
                    {t.toUpperCase()}
                  </option>
                ))}
              </select>
            </Field2>
            <Field2 label="ORDER">
              <input
                data-testid="show-order-input"
                type="number"
                className={fieldCls}
                value={show.order}
                onChange={(e) => set({ order: e.target.value })}
              />
            </Field2>
          </div>

          <Field2 label={`DESCRIPTION * (${(show.description || "").length}/600)`}>
            <textarea
              data-testid="show-description-input"
              className={fieldCls + " resize-none min-h-[110px]"}
              value={show.description}
              onChange={(e) => set({ description: e.target.value })}
              maxLength={600}
              placeholder="A short pitch — what makes this show different?"
            />
          </Field2>

          <Field2 label="HERO IMAGE URL (OPTIONAL)">
            <input
              data-testid="show-image-input"
              className={fieldCls}
              value={show.image_url || ""}
              onChange={(e) => set({ image_url: e.target.value })}
              placeholder="https://…"
            />
          </Field2>
        </div>

        <div className="px-6 sm:px-8 py-5 border-t border-white/10 flex items-center justify-between gap-3 bg-black/40">
          <button
            onClick={onCancel}
            className="font-accent text-xs tracking-[0.22em] text-white/60 hover:text-white"
          >
            CANCEL
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="show-save-btn"
            className="flex items-center gap-2 bg-[#FF2D9C] text-white font-black px-6 py-3 uppercase tracking-[0.22em] text-xs glow-pink hover:scale-[1.02] transition-all disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            {isNew ? "CREATE" : "SAVE"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

const fieldCls =
  "w-full bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/40 px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none transition-colors";

function Field2({ label, children }) {
  return (
    <div>
      <label className="block font-accent text-[10px] tracking-[0.22em] text-white/55 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}

// ============================================================
// INTAKES ADMIN — Culture Files / Events / Reenactment
// + Tagging & Matching: Story → Reenactment Talent
// ============================================================
const INTAKE_TABS = [
  { key: "culture-files", label: "Culture Files" },
  { key: "events", label: "We Outside (Events)" },
  { key: "reenactment", label: "Reenactment Talent" },
  { key: "food", label: "Food Vendors" },
  { key: "performers", label: "Performers" },
  { key: "content-creators", label: "Content Creators" },
  { key: "dating", label: "Love in the Fast Lane" },
  { key: "set-speaks", label: "The Set Speaks" },
  { key: "booked-outside", label: "Booked & Outside" },
  { key: "contact", label: "Live Culture Chat" },
];

const INTAKE_STATUS_OPTIONS = ["new", "reviewed", "shortlisted", "rejected"];

// ---------- The Set Speaks — Highlight Reel taxonomy ----------
// Keep keys in sync with backend HIGHLIGHT_TYPES / EPISODE_THEMES.
const HIGHLIGHT_TYPES = [
  { key: "strongest_personality", label: "Strongest Personality" },
  { key: "emotional_story", label: "Emotional Story" },
  { key: "controversial_take", label: "Controversial Take" },
  { key: "og_perspective", label: "OG Perspective" },
  { key: "viral_soundbite", label: "Viral Soundbite" },
  { key: "trailer_moment", label: "Trailer Moment" },
];

const EPISODE_THEMES = [
  { key: "brotherhood", label: "Brotherhood" },
  { key: "women_in_the_set", label: "Women in the Set" },
  { key: "social_media_era", label: "Social Media Era" },
  { key: "protocol", label: "Protocol" },
  { key: "politics", label: "Politics" },
  { key: "old_school_vs_new_school", label: "Old School vs New School" },
  { key: "leadership", label: "Leadership" },
  { key: "set_history", label: "Set History" },
  { key: "community_work", label: "Community Work" },
  { key: "club_expansion", label: "Club Expansion" },
  { key: "respect_and_discipline", label: "Respect & Discipline" },
];

const HIGHLIGHT_TYPE_LABEL = Object.fromEntries(
  HIGHLIGHT_TYPES.map((t) => [t.key, t.label]),
);
const EPISODE_THEME_LABEL = Object.fromEntries(
  EPISODE_THEMES.map((t) => [t.key, t.label]),
);

function IntakesAdmin({ token }) {
  const [kind, setKind] = useState("culture-files");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null); // intake row for detail
  const [matchingFor, setMatchingFor] = useState(null); // story row for matching
  const [chemistryFor, setChemistryFor] = useState(null); // LFL applicant for chemistry read
  const [reelView, setReelView] = useState(false); // The Set Speaks → highlight reel view

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = {};
      if (tagFilter) params.tag = tagFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await authClient(token).get(`/admin/intake/${kind}`, {
        params,
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setErr("Could not load intakes.");
    } finally {
      setLoading(false);
    }
  }, [token, kind, tagFilter, statusFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (r.full_name || "").toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q) ||
      (r.city || "").toLowerCase().includes(q) ||
      (r.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  });

  const allTags = Array.from(
    new Set(rows.flatMap((r) => r.tags || [])),
  ).sort();

  if (selected) {
    return (
      <IntakeDetail
        token={token}
        kind={kind}
        rowId={selected.id}
        onBack={() => {
          setSelected(null);
          fetchRows();
        }}
        onMatch={() => {
          setMatchingFor(selected);
          setSelected(null);
        }}
        canMatch={kind === "culture-files"}
        onChemistry={() => {
          setChemistryFor(selected);
          setSelected(null);
        }}
        canChemistry={kind === "dating"}
      />
    );
  }

  if (matchingFor) {
    return (
      <MatchingView
        token={token}
        story={matchingFor}
        onBack={() => setMatchingFor(null)}
      />
    );
  }

  if (chemistryFor) {
    return (
      <ChemistryReadView
        token={token}
        applicant={chemistryFor}
        onBack={() => setChemistryFor(null)}
      />
    );
  }

  if (reelView) {
    return (
      <HighlightReelView
        token={token}
        onBack={() => setReelView(false)}
        onOpenApplicant={(applicantId) => {
          setReelView(false);
          setSelected({ id: applicantId });
        }}
      />
    );
  }

  return (
    <main
      className="max-w-7xl mx-auto px-6 py-8"
      data-testid="intakes-admin"
    >
      <div className="mb-6 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="font-accent text-xs text-[#00E5FF] mb-1 tracking-[0.22em]">
            CASTING / INTAKES
          </div>
          <h1 className="font-heading text-3xl font-black">
            Custom intakes & talent pool
          </h1>
        </div>
        {isDocKind(kind) && (
          <button
            onClick={() => setReelView(true)}
            data-testid="open-highlight-reel-btn"
            className="inline-flex items-center gap-2 bg-[#C8A24A] text-black font-black px-4 py-2.5 uppercase tracking-[0.22em] text-[11px] hover:scale-[1.02] transition-transform"
          >
            <Star className="w-3.5 h-3.5" /> Highlight Reel
          </button>
        )}
      </div>

      {/* Kind sub-tabs */}
      <div
        className="flex flex-wrap gap-1 mb-5 border-b border-white/10 pb-3"
        data-testid="intake-kind-tabs"
      >
        {INTAKE_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              if (t.key === kind) return;
              // Clear all per-tab state before switching kind so a slow
              // /admin/intake/{kind} response can't leak stale rows from
              // the previous tab into the new one (root cause of the
              // inline-PATCH 404 reported in iter 8).
              setRows([]);
              setLoading(true);
              setSelected(null);
              setMatchingFor(null);
              setChemistryFor(null);
              setReelView(false);
              setTagFilter("");
              setStatusFilter("");
              setSearch("");
              setErr("");
              setKind(t.key);
            }}
            data-testid={`intake-kind-${t.key}`}
            className={`px-4 py-2 font-accent text-[11px] tracking-[0.22em] border transition-colors ${
              kind === t.key
                ? "border-[#FF2D9C] text-[#FF2D9C] bg-[#FF2D9C]/5"
                : "border-white/10 text-white/55 hover:text-white hover:border-white/30"
            }`}
          >
            {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, city, tag…"
            data-testid="intake-search"
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/30 outline-none transition-colors"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="intake-status-filter"
          className="px-3 py-2.5 text-sm bg-black/40 border border-white/10 outline-none focus:border-[#00E5FF]"
        >
          <option value="">ALL STATUSES</option>
          {statusOptionsFor(kind).map((s) => (
            <option key={s} value={s}>
              {(isDocKind(kind)
                ? DOC_STATUS_META[s].label
                : s.toUpperCase())}
            </option>
          ))}
        </select>
        {tagFilter && (
          <button
            onClick={() => setTagFilter("")}
            data-testid="intake-clear-tag"
            className="px-3 py-2.5 text-xs font-accent tracking-widest border border-[#FF2D9C]/50 text-[#FF2D9C] hover:bg-[#FF2D9C]/10"
          >
            CLEAR TAG · #{tagFilter}
          </button>
        )}
      </div>

      {/* Tag chips */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-5" data-testid="intake-tag-chips">
          {allTags.slice(0, 24).map((t) => (
            <button
              key={t}
              onClick={() => setTagFilter(t === tagFilter ? "" : t)}
              data-testid={`tag-chip-${t}`}
              className={`px-2 py-1 font-accent text-[10px] tracking-widest border transition-colors ${
                t === tagFilter
                  ? "border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/10"
                  : "border-white/10 text-white/60 hover:border-white/30"
              }`}
            >
              #{t}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-white/60 py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : err ? (
        <div className="text-[#FF2D9C] flex items-center gap-2 py-12 justify-center">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-white/10 py-16 text-center text-white/50">
          No submissions yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="intake-rows">
          {filtered.map((r) => (
            <IntakeRow
              key={r.id}
              row={r}
              kind={kind}
              token={token}
              onClick={() => setSelected(r)}
              onStatusChanged={(id, next) => {
                setRows((prev) =>
                  prev.map((x) => (x.id === id ? { ...x, status: next } : x)),
                );
              }}
            />
          ))}
        </div>
      )}
    </main>
  );
}

function IntakeRow({ row, kind, token, onClick, onStatusChanged }) {
  const status = row.status || "new";
  const meta = statusMetaFor(kind, status);
  const Icon = meta.Icon;
  const showInlineStatus = isDocKind(kind);
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusErr, setStatusErr] = useState("");

  const handleStatusChange = async (e) => {
    e.stopPropagation();
    const next = e.target.value;
    if (!next || next === status) return;
    setSavingStatus(true);
    setStatusErr("");
    try {
      await authClient(token).patch(`/admin/intake/${kind}/${row.id}`, {
        status: next,
      });
      onStatusChanged?.(row.id, next);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("inline status update failed", err);
      setStatusErr("Could not save — try again.");
      // auto-clear after a few seconds
      setTimeout(() => setStatusErr(""), 4000);
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      data-testid={`intake-row-${row.id}`}
      className="w-full text-left border border-white/10 bg-white/[0.02] hover:border-[#FF2D9C]/40 hover:bg-white/[0.04] transition-colors p-4 group cursor-pointer"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-accent tracking-widest border ${meta.cls}`}
              data-testid={`intake-row-status-badge-${row.id}`}
            >
              <Icon className="w-3 h-3" /> {meta.label}
            </span>
            <span className="text-white/30 text-xs">·</span>
            <span className="text-white/55 text-xs font-accent tracking-widest">
              {fmtDate(row.created_at)}
            </span>
            {kind === "events" && row.data?.event_name ? (
              <>
                <span className="text-white/30 text-xs">·</span>
                <span className="text-[#00E5FF] text-xs font-accent tracking-widest">
                  {row.data.event_name}
                </span>
              </>
            ) : null}
          </div>
          <div className="font-heading font-black text-lg text-white mb-1 truncate">
            {row.full_name || "—"}
          </div>
          <div className="text-xs text-white/55 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1">
              <Mail className="w-3 h-3" /> {row.email || "—"}
            </span>
            <span className="flex items-center gap-1">
              <Phone className="w-3 h-3" /> {row.phone || "—"}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {row.city || "—"}, {row.state || "—"}
            </span>
            {row.photos_count ? (
              <span>📷 {row.photos_count}</span>
            ) : null}
            {row.video_file_id || row.video_url ? <span>🎥 video</span> : null}
          </div>
          {row.tags && row.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {row.tags.slice(0, 8).map((t) => (
                <span
                  key={t}
                  className="px-1.5 py-0.5 text-[10px] font-accent tracking-widest border border-[#FF2D9C]/40 text-[#FF2D9C] bg-[#FF2D9C]/5"
                >
                  #{t}
                </span>
              ))}
              {row.tags.length > 8 && (
                <span className="text-[10px] text-white/40">
                  +{row.tags.length - 8}
                </span>
              )}
            </div>
          )}
        </div>

        {showInlineStatus && (
          <div
            className="shrink-0"
            onClick={(e) => e.stopPropagation()}
            data-testid={`intake-row-inline-status-${row.id}`}
          >
            <label className="block font-accent text-[10px] tracking-widest text-white/45 mb-1 text-right">
              PRODUCTION STATUS
            </label>
            <div className="flex items-center gap-2 justify-end">
              <span
                className={`w-2 h-2 rounded-full ${meta.dot || "bg-white/40"}`}
                aria-hidden
              />
              <select
                value={status}
                onChange={handleStatusChange}
                disabled={savingStatus}
                data-testid={`intake-row-status-select-${row.id}`}
                className={`px-2 py-1.5 text-[11px] font-accent tracking-widest bg-black/60 border outline-none focus:border-[#00E5FF] uppercase ${
                  savingStatus
                    ? "opacity-60 border-white/10"
                    : "border-white/20 hover:border-white/40"
                }`}
              >
                {DOC_STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {DOC_STATUS_META[s].label}
                  </option>
                ))}
              </select>
              {savingStatus && (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-white/50" />
              )}
            </div>
            {statusErr && (
              <div
                className="text-[10px] font-accent tracking-widest text-red-400 mt-1 text-right"
                data-testid={`intake-row-status-err-${row.id}`}
              >
                {statusErr}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function IntakeDetail({
  token,
  kind,
  rowId,
  onBack,
  onMatch,
  canMatch,
  onChemistry,
  canChemistry,
}) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [savingStatus, setSavingStatus] = useState("");

  useEffect(() => {
    setLoading(true);
    authClient(token)
      .get(`/admin/intake/${kind}/${rowId}`)
      .then((r) => setDoc(r.data))
      .catch(() => setErr("Could not load."))
      .finally(() => setLoading(false));
  }, [token, kind, rowId]);

  const updateStatus = async (status) => {
    setSavingStatus(status);
    try {
      await authClient(token).patch(`/admin/intake/${kind}/${rowId}`, {
        status,
      });
      setDoc((d) => ({ ...d, status }));
    } catch (e) {
      // ignore
    } finally {
      setSavingStatus("");
    }
  };

  return (
    <main
      className="max-w-5xl mx-auto px-6 py-8"
      data-testid="intake-detail"
    >
      <button
        onClick={onBack}
        data-testid="intake-detail-back"
        className="flex items-center gap-2 text-white/60 hover:text-white mb-6 font-accent text-xs tracking-widest"
      >
        <ArrowLeft className="w-4 h-4" /> BACK TO INTAKES
      </button>

      {loading ? (
        <div className="flex items-center gap-2 text-white/60 py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : err || !doc ? (
        <div className="text-[#FF2D9C] flex items-center gap-2 py-12 justify-center">
          <AlertCircle className="w-4 h-4" /> {err || "Not found"}
        </div>
      ) : (
        <>
          <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-accent text-xs text-[#FF2D9C] mb-1 tracking-[0.22em]">
                {(kind || "").toUpperCase().replace(/-/g, " ")} · INTAKE DETAIL
              </div>
              <h1 className="font-heading text-3xl font-black">
                {doc.full_name || "—"}
              </h1>
              <div className="text-xs text-white/55 mt-1 flex flex-wrap gap-3">
                <span>{doc.email || "—"}</span>
                <span>·</span>
                <span>{doc.phone || "—"}</span>
                <span>·</span>
                <span>
                  {doc.city || "—"}, {doc.state || "—"}
                </span>
                <span>·</span>
                <span>{fmtDate(doc.created_at)}</span>
              </div>
            </div>
            {canMatch && (
              <button
                onClick={onMatch}
                data-testid="intake-detail-match"
                className="inline-flex items-center gap-2 bg-[#FF2D9C] text-white font-black px-4 py-2.5 uppercase tracking-[0.22em] text-[11px] glow-pink"
              >
                <Star className="w-3.5 h-3.5" /> Find Talent Matches
              </button>
            )}
            {canChemistry && (
              <button
                onClick={onChemistry}
                data-testid="intake-detail-chemistry"
                className="inline-flex items-center gap-2 bg-[#FF2D9C] text-white font-black px-4 py-2.5 uppercase tracking-[0.22em] text-[11px] glow-pink"
              >
                <Heart className="w-3.5 h-3.5" /> Chemistry Read
              </button>
            )}
          </div>

          {/* Status workflow */}
          <div className="flex flex-wrap gap-2 mb-6">
            {statusOptionsFor(kind).map((s) => {
              const active = (doc.status || "new") === s;
              const m = statusMetaFor(kind, s);
              return (
                <button
                  key={s}
                  onClick={() => updateStatus(s)}
                  disabled={!!savingStatus}
                  data-testid={`intake-status-${s}`}
                  className={`px-3 py-1.5 font-accent text-[10px] tracking-widest border transition-colors ${
                    active
                      ? m.cls + " bg-white/[0.04]"
                      : "border-white/10 text-white/55 hover:text-white hover:border-white/30"
                  } ${savingStatus === s ? "opacity-60" : ""}`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>

          {/* Highlight Reel editor (set-speaks only) */}
          {isDocKind(kind) && (
            <HighlightReelEditor token={token} applicantId={rowId} />
          )}

          {/* Tags */}
          {doc.tags && doc.tags.length > 0 && (
            <div className="mb-6">
              <div className="font-accent text-[11px] tracking-widest text-white/55 mb-2">
                TAGS
              </div>
              <div className="flex flex-wrap gap-1.5">
                {doc.tags.map((t) => (
                  <span
                    key={t}
                    className="px-2 py-0.5 text-[10px] font-accent tracking-widest border border-[#FF2D9C]/50 text-[#FF2D9C] bg-[#FF2D9C]/5"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Data */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(doc.data || {}).map(([k, v]) => (
              <div
                key={k}
                className="border border-white/10 bg-white/[0.02] p-3"
                data-testid={`intake-data-${k}`}
              >
                <div className="font-accent text-[10px] tracking-widest text-white/45 mb-1">
                  {k.replace(/_/g, " ").toUpperCase()}
                </div>
                <div className="text-sm text-white/85 whitespace-pre-wrap break-words">
                  {Array.isArray(v) ? v.join(", ") : String(v ?? "—")}
                </div>
              </div>
            ))}
          </div>

          {/* Video */}
          {(doc.video_file_id || doc.video_url) && (
            <div className="mt-6 border border-white/10 bg-white/[0.02] p-4">
              <div className="font-accent text-[11px] tracking-widest text-white/55 mb-2">
                VIDEO
              </div>
              {doc.video_file_id ? (
                <video
                  controls
                  className="w-full max-h-[480px] bg-black"
                  src={`${API}/applications/video/${doc.video_file_id}`}
                />
              ) : (
                <a
                  href={doc.video_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#00E5FF] inline-flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" /> {doc.video_url}
                </a>
              )}
            </div>
          )}
        </>
      )}
    </main>
  );
}

// ============================================================
// THE SET SPEAKS — Highlight Reel
// ============================================================
function HighlightReelEditor({ token, applicantId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [inReel, setInReel] = useState(false);
  const [types, setTypes] = useState([]);
  const [themes, setThemes] = useState([]);
  const [notes, setNotes] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setErr("");
    authClient(token)
      .get(`/admin/highlight-reel/${applicantId}`)
      .then((r) => {
        if (!active) return;
        const d = r.data || {};
        setInReel(true);
        setTypes(d.highlight_types || []);
        setThemes(d.episode_themes || []);
        setNotes(d.notes || "");
        setQuotes(d.quotes || []);
        setSavedAt(d.updated_at || "");
      })
      .catch((e) => {
        if (!active) return;
        if (e?.response?.status === 404) {
          setInReel(false);
          setTypes([]);
          setThemes([]);
          setNotes("");
          setQuotes([]);
          setSavedAt("");
        } else {
          setErr("Could not load highlight reel.");
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, applicantId]);

  const toggle = (arr, key) =>
    arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key];

  const save = async () => {
    setSaving(true);
    setErr("");
    // Strip empty quote rows before send
    const cleanQuotes = quotes
      .map((q) => ({
        text: (q.text || "").trim(),
        timestamp: (q.timestamp || "").trim(),
        source: q.source || "custom",
      }))
      .filter((q) => q.text.length >= 4);
    try {
      const res = await authClient(token).post("/admin/highlight-reel", {
        applicant_id: applicantId,
        kind: "set-speaks",
        highlight_types: types,
        episode_themes: themes,
        notes,
        quotes: cleanQuotes,
      });
      setInReel(true);
      setQuotes(res.data?.quotes || cleanQuotes);
      setSavedAt(res.data?.updated_at || new Date().toISOString());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail) && detail[0]?.msg) {
        setErr(`Could not save: ${detail[0].msg}`);
      } else {
        setErr("Could not save. Try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    setErr("");
    try {
      await authClient(token).delete(`/admin/highlight-reel/${applicantId}`);
      setInReel(false);
      setTypes([]);
      setThemes([]);
      setNotes("");
      setQuotes([]);
      setSavedAt("");
    } catch (e) {
      setErr("Could not remove. Try again.");
    } finally {
      setRemoving(false);
    }
  };

  const addQuote = () => {
    if (quotes.length >= 20) return;
    setQuotes((q) => [...q, { text: "", timestamp: "", source: "custom" }]);
  };
  const updateQuote = (i, patch) =>
    setQuotes((q) => q.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeQuote = (i) =>
    setQuotes((q) => q.filter((_, idx) => idx !== i));

  if (loading) {
    return (
      <div
        className="border border-[#C8A24A]/40 bg-[#C8A24A]/[0.04] p-4 mb-6 flex items-center gap-2 text-white/60"
        data-testid="highlight-reel-editor"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading highlight reel…
      </div>
    );
  }

  return (
    <div
      className="border border-[#C8A24A]/40 bg-gradient-to-b from-[#C8A24A]/[0.06] to-transparent p-5 mb-6"
      data-testid="highlight-reel-editor"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="font-accent text-[11px] tracking-[0.28em] text-[#C8A24A] mb-1">
            HIGHLIGHT REEL
          </div>
          <div className="font-heading font-black text-xl">
            {inReel ? "Pinned to the reel" : "Push to the reel"}
          </div>
          <div className="text-[11px] text-white/55 mt-1">
            Producers' shortlist for sizzle reels, episode pitches & trailer
            beats.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {inReel && (
            <button
              onClick={remove}
              disabled={removing || saving}
              data-testid="highlight-reel-remove"
              className="font-accent text-[10px] tracking-widest border border-red-500/40 text-red-400 hover:bg-red-500/10 px-3 py-2 disabled:opacity-50"
            >
              {removing ? "REMOVING…" : "REMOVE FROM REEL"}
            </button>
          )}
          <button
            onClick={save}
            disabled={saving || removing}
            data-testid="highlight-reel-save"
            className="inline-flex items-center gap-2 bg-[#C8A24A] text-black font-black px-4 py-2 uppercase tracking-[0.22em] text-[11px] disabled:opacity-60 hover:scale-[1.02] transition-transform"
          >
            {saving ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
              </>
            ) : inReel ? (
              <>Update Reel Tags</>
            ) : (
              <>
                <Star className="w-3.5 h-3.5" /> Push to Highlight Reel
              </>
            )}
          </button>
        </div>
      </div>

      {/* Highlight Type chips */}
      <div className="mb-5">
        <div className="font-accent text-[10px] tracking-widest text-white/55 mb-2">
          HIGHLIGHT TYPE
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="highlight-types-row">
          {HIGHLIGHT_TYPES.map((t) => {
            const active = types.includes(t.key);
            return (
              <button
                key={t.key}
                onClick={() => setTypes((cur) => toggle(cur, t.key))}
                data-testid={`highlight-type-${t.key}`}
                className={`px-2.5 py-1 text-[10px] font-accent tracking-widest border transition-colors ${
                  active
                    ? "border-[#FF2D9C] text-[#FF2D9C] bg-[#FF2D9C]/10"
                    : "border-white/15 text-white/55 hover:text-white hover:border-white/40"
                }`}
              >
                {t.label.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>

      {/* Episode Theme chips */}
      <div className="mb-5">
        <div className="font-accent text-[10px] tracking-widest text-white/55 mb-2">
          POTENTIAL EPISODE THEME
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="episode-themes-row">
          {EPISODE_THEMES.map((t) => {
            const active = themes.includes(t.key);
            return (
              <button
                key={t.key}
                onClick={() => setThemes((cur) => toggle(cur, t.key))}
                data-testid={`episode-theme-${t.key}`}
                className={`px-2.5 py-1 text-[10px] font-accent tracking-widest border transition-colors ${
                  active
                    ? "border-[#C8A24A] text-[#C8A24A] bg-[#C8A24A]/10"
                    : "border-white/15 text-white/55 hover:text-white hover:border-white/40"
                }`}
              >
                {t.label.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>

      {/* Producer notes */}
      <div>
        <div className="font-accent text-[10px] tracking-widest text-white/55 mb-2 flex items-center justify-between">
          <span>PRODUCER NOTES</span>
          <span className="text-white/35 normal-case">
            {notes.length}/4000
          </span>
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, 4000))}
          rows={4}
          placeholder="Soundbites, scene ideas, who they should be paired with on camera…"
          data-testid="highlight-reel-notes"
          className="w-full px-3 py-2.5 text-sm bg-black/40 border border-white/10 focus:border-[#C8A24A] outline-none resize-y"
        />
      </div>

      {/* Notable Quotes */}
      <div className="mt-6" data-testid="highlight-reel-quotes-section">
        <div className="font-accent text-[10px] tracking-widest text-white/55 mb-2 flex items-center justify-between">
          <span>NOTABLE QUOTES</span>
          <span className="text-white/35 normal-case">
            {quotes.length}/20
          </span>
        </div>
        {quotes.length === 0 && (
          <div
            className="border border-dashed border-[#C8A24A]/25 px-3 py-4 text-xs text-white/45 italic"
            data-testid="highlight-reel-quotes-empty"
          >
            No quotes yet. Click <span className="text-[#C8A24A]">Add Quote</span> to
            paste a soundbite, callback line, or controversial take with an
            optional video timestamp.
          </div>
        )}
        <div className="space-y-2.5">
          {quotes.map((q, i) => (
            <QuoteRow
              key={i}
              index={i}
              quote={q}
              onChange={(patch) => updateQuote(i, patch)}
              onRemove={() => removeQuote(i)}
            />
          ))}
        </div>
        <button
          onClick={addQuote}
          disabled={quotes.length >= 20}
          data-testid="highlight-reel-add-quote"
          className={`mt-3 inline-flex items-center gap-2 border border-[#C8A24A]/60 text-[#C8A24A] font-black px-3 py-2 uppercase tracking-[0.22em] text-[10px] hover:bg-[#C8A24A]/10 transition-colors ${
            quotes.length >= 20 ? "opacity-40 cursor-not-allowed" : ""
          }`}
        >
          <Plus className="w-3 h-3" /> Add Quote
        </button>
      </div>

      {savedAt && (
        <div
          className="mt-3 text-[10px] font-accent tracking-widest text-white/40"
          data-testid="highlight-reel-saved-at"
        >
          LAST SAVED · {fmtDate(savedAt)}
        </div>
      )}
      {err && (
        <div
          className="mt-3 flex items-center gap-2 text-red-400 text-xs"
          data-testid="highlight-reel-err"
        >
          <AlertCircle className="w-3.5 h-3.5" /> {err}
        </div>
      )}
    </div>
  );
}

function QuoteRow({ index, quote, onChange, onRemove }) {
  const text = quote.text || "";
  const ts = quote.timestamp || "";
  const source = quote.source || "custom";
  const tooShort = text.trim().length > 0 && text.trim().length < 4;
  const tooLong = text.length > 500;
  return (
    <div
      className={`border ${
        tooShort || tooLong ? "border-red-500/50" : "border-white/15"
      } bg-black/30 p-3`}
      data-testid={`highlight-reel-quote-${index}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-accent text-[10px] tracking-widest text-[#C8A24A]">
          QUOTE #{index + 1}
        </div>
        <button
          onClick={onRemove}
          data-testid={`highlight-reel-quote-remove-${index}`}
          className="font-accent text-[10px] tracking-widest text-red-400/80 hover:text-red-300 inline-flex items-center gap-1"
          type="button"
        >
          <Trash2 className="w-3 h-3" /> REMOVE
        </button>
      </div>
      <textarea
        value={text}
        onChange={(e) => onChange({ text: e.target.value.slice(0, 500) })}
        rows={2}
        placeholder='"They don&apos;t understand the patch. They wear it for clout."'
        data-testid={`highlight-reel-quote-text-${index}`}
        className="w-full px-2.5 py-2 text-sm bg-black/40 border border-white/10 focus:border-[#C8A24A] outline-none resize-y italic"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5">
          <span className="font-accent text-[9px] tracking-widest text-white/45">
            TS
          </span>
          <input
            type="text"
            value={ts}
            onChange={(e) => onChange({ timestamp: e.target.value.slice(0, 16) })}
            placeholder="02:14"
            data-testid={`highlight-reel-quote-ts-${index}`}
            className="w-20 px-2 py-1.5 text-xs font-mono bg-black/40 border border-white/10 focus:border-[#C8A24A] outline-none"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="font-accent text-[9px] tracking-widest text-white/45">
            SOURCE
          </span>
          <select
            value={source}
            onChange={(e) => onChange({ source: e.target.value })}
            data-testid={`highlight-reel-quote-source-${index}`}
            className="px-2 py-1.5 text-[11px] font-accent tracking-widest bg-black/40 border border-white/10 outline-none focus:border-[#C8A24A] uppercase"
          >
            <option value="custom">CUSTOM</option>
            <option value="application_answer">APPLICATION ANSWER</option>
            <option value="intro_video">INTRO VIDEO</option>
          </select>
        </div>
        <div className="ml-auto text-[10px] font-accent tracking-widest text-white/40">
          {text.length}/500
        </div>
      </div>
      {(tooShort || tooLong) && (
        <div
          className="mt-1 text-[10px] font-accent tracking-widest text-red-400"
          data-testid={`highlight-reel-quote-err-${index}`}
        >
          {tooShort
            ? "QUOTE MUST BE AT LEAST 4 CHARACTERS"
            : "QUOTE EXCEEDS 500 CHARACTERS"}
        </div>
      )}
    </div>
  );
}

function HighlightReelView({ token, onBack, onOpenApplicant }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [themeFilter, setThemeFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = {};
      if (themeFilter) params.theme = themeFilter;
      if (typeFilter) params.highlight_type = typeFilter;
      const res = await authClient(token).get("/admin/highlight-reel", {
        params,
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setErr("Could not load highlight reel.");
    } finally {
      setLoading(false);
    }
  }, [token, themeFilter, typeFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (r.applicant_name || "").toLowerCase().includes(q) ||
      (r.applicant_email || "").toLowerCase().includes(q) ||
      (r.notes || "").toLowerCase().includes(q) ||
      (r.quotes || []).some((qt) =>
        (qt.text || "").toLowerCase().includes(q),
      )
    );
  });

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/highlight-reel/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "text/csv" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = "set-speaks-highlight-reel.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("export failed", e);
    }
  };

  return (
    <main
      className="max-w-7xl mx-auto px-6 py-8"
      data-testid="highlight-reel-view"
    >
      <button
        onClick={onBack}
        data-testid="highlight-reel-back"
        className="flex items-center gap-2 text-white/60 hover:text-white mb-6 font-accent text-xs tracking-widest"
      >
        <ArrowLeft className="w-4 h-4" /> BACK TO INTAKES
      </button>

      <div className="mb-6 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="font-accent text-xs text-[#C8A24A] mb-1 tracking-[0.22em]">
            THE SET SPEAKS · PRODUCTION
          </div>
          <h1 className="font-heading text-3xl font-black">Highlight Reel</h1>
          <div className="text-sm text-white/55 mt-1">
            Pinned voices, soundbites, and trailer moments for the doc.
          </div>
        </div>
        <button
          onClick={exportCsv}
          data-testid="highlight-reel-export-csv"
          className="inline-flex items-center gap-2 border border-[#C8A24A]/60 text-[#C8A24A] font-black px-4 py-2.5 uppercase tracking-[0.22em] text-[11px] hover:bg-[#C8A24A]/10"
        >
          Export CSV
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, notes, quotes…"
            data-testid="highlight-reel-search"
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-black/40 border border-white/10 focus:border-[#C8A24A] outline-none"
          />
        </div>
        <select
          value={themeFilter}
          onChange={(e) => setThemeFilter(e.target.value)}
          data-testid="highlight-reel-theme-filter"
          className="px-3 py-2.5 text-sm bg-black/40 border border-white/10 outline-none focus:border-[#C8A24A]"
        >
          <option value="">ALL THEMES</option>
          {EPISODE_THEMES.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label.toUpperCase()}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          data-testid="highlight-reel-type-filter"
          className="px-3 py-2.5 text-sm bg-black/40 border border-white/10 outline-none focus:border-[#C8A24A]"
        >
          <option value="">ALL HIGHLIGHT TYPES</option>
          {HIGHLIGHT_TYPES.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label.toUpperCase()}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/60 py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : err ? (
        <div className="text-[#FF2D9C] flex items-center gap-2 py-12 justify-center">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[#C8A24A]/30 py-16 text-center text-white/55">
          {rows.length === 0 ? (
            <>
              No applicants on the reel yet. Open a Set Speaks applicant and
              click <span className="text-[#C8A24A]">Push to Highlight Reel</span>.
            </>
          ) : (
            <>No matches for the current filters.</>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="highlight-reel-rows">
          {filtered.map((r) => (
            <HighlightReelRow
              key={r.applicant_id}
              row={r}
              onOpen={() => onOpenApplicant?.(r.applicant_id)}
            />
          ))}
        </div>
      )}
    </main>
  );
}

function HighlightReelRow({ row, onOpen }) {
  return (
    <div
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen?.();
        }
      }}
      data-testid={`highlight-reel-row-${row.applicant_id}`}
      className="w-full text-left border border-[#C8A24A]/30 bg-white/[0.02] hover:border-[#C8A24A] hover:bg-[#C8A24A]/[0.04] transition-colors p-4 cursor-pointer"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-heading font-black text-lg text-white truncate">
            {row.applicant_name || "—"}
          </div>
          <div className="text-xs text-white/55 flex items-center gap-3 flex-wrap mt-1">
            <span className="flex items-center gap-1">
              <Mail className="w-3 h-3" /> {row.applicant_email || "—"}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {row.applicant_city || "—"},{" "}
              {row.applicant_state || "—"}
            </span>
            <span className="font-accent tracking-widest text-white/40">
              {fmtDate(row.updated_at)}
            </span>
          </div>

          {row.highlight_types?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {row.highlight_types.map((k) => (
                <span
                  key={k}
                  className="px-1.5 py-0.5 text-[10px] font-accent tracking-widest border border-[#FF2D9C]/50 text-[#FF2D9C] bg-[#FF2D9C]/5"
                >
                  {(HIGHLIGHT_TYPE_LABEL[k] || k).toUpperCase()}
                </span>
              ))}
            </div>
          )}
          {row.episode_themes?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {row.episode_themes.map((k) => (
                <span
                  key={k}
                  className="px-1.5 py-0.5 text-[10px] font-accent tracking-widest border border-[#C8A24A]/60 text-[#C8A24A] bg-[#C8A24A]/5"
                >
                  {(EPISODE_THEME_LABEL[k] || k).toUpperCase()}
                </span>
              ))}
            </div>
          )}
          {row.notes && (
            <div className="mt-2 text-xs text-white/70 line-clamp-2 italic">
              "{row.notes}"
            </div>
          )}
          {row.quotes?.length > 0 && (
            <div
              className="mt-3 border-l-2 border-[#C8A24A]/60 pl-3 space-y-1"
              data-testid={`highlight-reel-row-quotes-${row.applicant_id}`}
            >
              <div className="font-accent text-[9px] tracking-[0.28em] text-[#C8A24A]/80">
                {row.quotes.length} QUOTE{row.quotes.length === 1 ? "" : "S"}
              </div>
              {row.quotes.slice(0, 2).map((q, i) => (
                <div
                  key={i}
                  className="text-xs text-white/75 italic line-clamp-1"
                >
                  {q.timestamp ? (
                    <span className="font-mono not-italic text-white/40 mr-1.5">
                      [{q.timestamp}]
                    </span>
                  ) : null}
                  "{q.text}"
                </div>
              ))}
              {row.quotes.length > 2 && (
                <div className="text-[10px] font-accent tracking-widest text-white/40">
                  + {row.quotes.length - 2} MORE
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MatchingView({ token, story, onBack }) {
  const [talent, setTalent] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const storyTags = (story.tags || []).map((t) => t.toLowerCase());

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      authClient(token).get(`/admin/intake/reenactment`),
      authClient(token).get(`/admin/intake/events`),
    ])
      .then(([t, e]) => {
        if (!active) return;
        setTalent(Array.isArray(t.data) ? t.data : []);
        setEvents(Array.isArray(e.data) ? e.data : []);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const scored = (rows) =>
    rows
      .map((r) => {
        const tags = (r.tags || []).map((t) => t.toLowerCase());
        const overlap = tags.filter((t) => storyTags.includes(t));
        return { row: r, overlap };
      })
      .filter((x) => x.overlap.length > 0)
      .sort((a, b) => b.overlap.length - a.overlap.length);

  const matchedTalent = scored(talent);
  const sameRegionEvents = events.filter(
    (e) =>
      (e.state || "").toLowerCase() === (story.state || "").toLowerCase() &&
      (story.state || "").length > 0,
  );

  return (
    <main className="max-w-7xl mx-auto px-6 py-8" data-testid="matching-view">
      <button
        onClick={onBack}
        data-testid="matching-back"
        className="flex items-center gap-2 text-white/60 hover:text-white mb-6 font-accent text-xs tracking-widest"
      >
        <ArrowLeft className="w-4 h-4" /> BACK
      </button>

      <div className="mb-6">
        <div className="font-accent text-xs text-[#FF2D9C] mb-1 tracking-[0.22em]">
          MATCHING · STORY → NEEDED TALENT
        </div>
        <h1 className="font-heading text-3xl font-black">
          {story.full_name}
          <span className="text-white/40"> · {story.city}, {story.state}</span>
        </h1>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {storyTags.map((t) => (
            <span
              key={t}
              className="px-2 py-0.5 text-[10px] font-accent tracking-widest border border-[#FF2D9C]/50 text-[#FF2D9C] bg-[#FF2D9C]/5"
            >
              #{t}
            </span>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/60 py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Searching matches…
        </div>
      ) : (
        <>
          <section className="mb-10" data-testid="matched-talent">
            <h2 className="font-heading text-xl font-black mb-3">
              Talent matches
              <span className="text-white/40 text-sm font-normal ml-2">
                {matchedTalent.length} found by tag overlap
              </span>
            </h2>
            {matchedTalent.length === 0 ? (
              <div className="border border-dashed border-white/10 py-10 text-center text-white/50 text-sm">
                No reenactment talent shares any tag with this story yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {matchedTalent.map(({ row, overlap }) => (
                  <div
                    key={row.id}
                    className="border border-white/10 bg-white/[0.02] p-4"
                    data-testid={`matched-talent-${row.id}`}
                  >
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <div className="font-heading font-black text-base text-white truncate">
                        {row.full_name}
                      </div>
                      <span className="text-[10px] font-accent tracking-widest text-[#00E5FF] border border-[#00E5FF]/40 px-2 py-0.5">
                        {overlap.length} TAG MATCH
                      </span>
                    </div>
                    <div className="text-xs text-white/55 mb-2">
                      {row.city}, {row.state} · {row.email}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {overlap.map((t) => (
                        <span
                          key={t}
                          className="px-1.5 py-0.5 text-[10px] font-accent tracking-widest border border-[#00E5FF]/50 text-[#00E5FF] bg-[#00E5FF]/5"
                        >
                          #{t}
                        </span>
                      ))}
                      {(row.tags || [])
                        .filter((t) => !overlap.includes(t.toLowerCase()))
                        .slice(0, 6)
                        .map((t) => (
                          <span
                            key={t}
                            className="px-1.5 py-0.5 text-[10px] font-accent tracking-widest border border-white/15 text-white/55"
                          >
                            #{t}
                          </span>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section data-testid="matched-events">
            <h2 className="font-heading text-xl font-black mb-3">
              Events in {story.state || "—"}
              <span className="text-white/40 text-sm font-normal ml-2">
                {sameRegionEvents.length} found
              </span>
            </h2>
            {sameRegionEvents.length === 0 ? (
              <div className="border border-dashed border-white/10 py-10 text-center text-white/50 text-sm">
                No events submitted in this region yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {sameRegionEvents.map((e) => (
                  <div
                    key={e.id}
                    className="border border-white/10 bg-white/[0.02] p-4"
                    data-testid={`matched-event-${e.id}`}
                  >
                    <div className="font-heading font-black text-base text-white truncate">
                      {e.data?.event_name || e.full_name}
                    </div>
                    <div className="text-xs text-white/55 mt-1">
                      Organizer · {e.full_name} · {e.city}, {e.state}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </main>
  );
}



// ============================================================
// CHEMISTRY READ VIEW — LFL casting pair finder
// ============================================================

const LANES = ["Trucking", "Biking", "Cars", "Boating"];
const LOOKING_FOR_OPTIONS = [
  "Real relationship",
  "Something fun",
  "Open to whatever happens",
  "Not sure yet",
];
const STATUS_OPTIONS_DATING = ["Yes", "It's complicated", "No (but open 👀)"];
const DRAMA_OPTIONS = [
  "I'm calm and drama-free",
  "I mind my business… mostly",
  "I match energy",
  "I bring the chaos 😈",
];

/** Score + rationale for a candidate match vs. applicant. */
function scoreChemistry(applicant, cand) {
  const a = applicant.data || {};
  const b = cand.data || {};
  const aLanes = new Set((a.lanes || []).map((x) => x.toLowerCase()));
  const bLanes = new Set((b.lanes || []).map((x) => x.toLowerCase()));
  const laneOverlap = [...aLanes].filter((l) => bLanes.has(l));
  if (laneOverlap.length === 0) return null; // hard filter: must share at least one lane

  let score = laneOverlap.length * 3;
  const reasons = [];
  const caution = [];
  let dynamic = "";

  reasons.push(
    `Shared lane${laneOverlap.length > 1 ? "s" : ""}: ${laneOverlap.join(", ")}`,
  );

  // Looking for — alignment
  if (a.looking_for && b.looking_for) {
    if (a.looking_for === b.looking_for) {
      score += 3;
      reasons.push(`Both looking for ${a.looking_for.toLowerCase()}`);
    } else if (
      (a.looking_for === "Real relationship" &&
        b.looking_for === "Open to whatever happens") ||
      (b.looking_for === "Real relationship" &&
        a.looking_for === "Open to whatever happens")
    ) {
      score += 1;
      caution.push("One wants real, one is open — chemistry tension");
    } else if (
      a.looking_for === "Something fun" &&
      b.looking_for === "Real relationship"
    ) {
      score -= 2;
      caution.push("Mismatched intent (fun vs. real)");
    } else {
      score += 1;
    }
  }

  // State proximity
  if (
    applicant.state &&
    cand.state &&
    applicant.state.toLowerCase() === cand.state.toLowerCase()
  ) {
    score += 2;
    reasons.push(`Both in ${cand.state}`);
  } else if (applicant.state && cand.state) {
    reasons.push(`${applicant.state} × ${cand.state} — travel dynamic`);
  }

  // Personality chemistry
  const drama = {
    [DRAMA_OPTIONS[0]]: 0, // calm
    [DRAMA_OPTIONS[1]]: 1, // mind biz
    [DRAMA_OPTIONS[2]]: 2, // match energy
    [DRAMA_OPTIONS[3]]: 3, // chaos
  };
  const ad = drama[a.drama_level];
  const bd = drama[b.drama_level];
  if (ad !== undefined && bd !== undefined) {
    const diff = Math.abs(ad - bd);
    if (diff === 0 && ad >= 2) {
      score += 3;
      dynamic = "Matched high-energy — chaos × chaos";
    } else if (diff === 3) {
      score += 3;
      dynamic = "Opposites-attract: peace × chaos";
    } else if (diff === 1 || diff === 2) {
      score += 2;
      dynamic = "Balanced personality blend";
    }
  }

  // Competition energy flag
  if (
    a.competition_energy === "I always win 😎" &&
    b.competition_energy === "I always win 😎"
  ) {
    score += 2;
    caution.push("Two alphas — guaranteed tension");
  }

  // Turn-off vs green flags conflict (crude text overlap)
  const aTurnOff = (a.turn_off || "").toLowerCase();
  const bGreen = (b.green_flags || "").toLowerCase();
  const bTurnOff = (b.turn_off || "").toLowerCase();
  const aGreen = (a.green_flags || "").toLowerCase();
  const aTurnOffWords = new Set(
    aTurnOff.split(/\W+/).filter((w) => w.length > 3),
  );
  const bGreenWords = new Set(
    bGreen.split(/\W+/).filter((w) => w.length > 3),
  );
  const conflicts1 = [...aTurnOffWords].filter((w) => bGreenWords.has(w));
  const bTurnOffWords = new Set(
    bTurnOff.split(/\W+/).filter((w) => w.length > 3),
  );
  const aGreenWords = new Set(
    aGreen.split(/\W+/).filter((w) => w.length > 3),
  );
  const conflicts2 = [...bTurnOffWords].filter((w) => aGreenWords.has(w));
  const conflicts = [...conflicts1, ...conflicts2];
  if (conflicts.length > 0) {
    score -= 3;
    caution.push(
      `Red flag overlap: "${conflicts.slice(0, 2).join('", "')}"`,
    );
  }

  // Multi-dating chemistry
  if (
    a.multi_dating === "Yes 😏" &&
    b.multi_dating === "No"
  ) {
    score -= 1;
    caution.push("Multi-dater × monogamist — drama likely");
  }

  return {
    score,
    reason: reasons.join(" · "),
    dynamic: dynamic || "Scripted-casting potential",
    caution,
  };
}

function ChemistryReadView({ token, applicant, onBack }) {
  const [candidates, setCandidates] = useState([]);
  const [savedMatches, setSavedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [savingId, setSavingId] = useState("");
  const [filters, setFilters] = useState({
    lane: "",
    state: "",
    looking_for: "",
    status: "",
    drama: "",
  });

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      authClient(token).get(`/admin/intake/dating`, { params: { limit: 500 } }),
      authClient(token).get(`/admin/dating/matches`, {
        params: { applicant_id: applicant.id },
      }),
    ])
      .then(([c, m]) => {
        if (!active) return;
        setCandidates(
          (Array.isArray(c.data) ? c.data : []).filter(
            (x) => x.id !== applicant.id,
          ),
        );
        setSavedMatches(Array.isArray(m.data) ? m.data : []);
      })
      .catch(() => active && setErr("Could not load candidates."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, applicant.id]);

  const a = applicant.data || {};

  const filteredCandidates = candidates.filter((c) => {
    const cd = c.data || {};
    if (
      filters.lane &&
      !((cd.lanes || []).map((l) => l.toLowerCase()).includes(filters.lane.toLowerCase()))
    )
      return false;
    if (filters.state && (c.state || "").toLowerCase() !== filters.state.toLowerCase())
      return false;
    if (filters.looking_for && cd.looking_for !== filters.looking_for)
      return false;
    if (filters.status && cd.status !== filters.status) return false;
    if (filters.drama && cd.drama_level !== filters.drama) return false;
    return true;
  });

  const scored = filteredCandidates
    .map((c) => ({ cand: c, s: scoreChemistry(applicant, c) }))
    .filter((x) => x.s !== null)
    .sort((a, b) => b.s.score - a.s.score)
    .slice(0, 5);

  const savedIds = new Set(savedMatches.map((m) => m.match_id));

  const saveMatch = async (cand, reason, dynamic) => {
    setSavingId(cand.id);
    try {
      const { data } = await authClient(token).post(`/admin/dating/match`, {
        applicant_id: applicant.id,
        match_id: cand.id,
        chemistry_reason: reason,
        dynamic,
      });
      setSavedMatches((prev) => {
        const exists = prev.some((m) => m.match_id === cand.id);
        return exists ? prev : [data, ...prev];
      });
    } catch (e) {
      // ignore
    } finally {
      setSavingId("");
    }
  };

  const uniqueStates = Array.from(
    new Set(candidates.map((c) => c.state).filter(Boolean)),
  ).sort();

  return (
    <main
      className="max-w-7xl mx-auto px-6 py-8"
      data-testid="chemistry-read-view"
    >
      <button
        onClick={onBack}
        data-testid="chemistry-back"
        className="flex items-center gap-2 text-white/60 hover:text-white mb-6 font-accent text-xs tracking-widest"
      >
        <ArrowLeft className="w-4 h-4" /> BACK
      </button>

      <div className="mb-2 font-accent text-xs text-[#FF2D9C] tracking-[0.22em]">
        LOVE IN THE FAST LANE · CHEMISTRY READ
      </div>
      <h1 className="font-heading text-3xl md:text-4xl font-black mb-1">
        {applicant.full_name}
        <span className="text-white/40 font-normal">
          {" "}
          · {applicant.city || "—"}, {applicant.state || "—"}
        </span>
      </h1>

      {/* Applicant profile summary */}
      <section
        className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6 mb-8"
        data-testid="applicant-summary"
      >
        <ProfileCell label="Lanes" value={(a.lanes || []).join(", ") || "—"} />
        <ProfileCell label="Looking For" value={a.looking_for || "—"} />
        <ProfileCell label="Current Status" value={a.status || "—"} />
        <ProfileCell label="Attracted To" value={a.attracted_to || "—"} wide />
        <ProfileCell label="Peace or Chaos" value={a.drama_level || "—"} />
        <ProfileCell
          label="Competition Energy"
          value={a.competition_energy || "—"}
        />
        <ProfileCell label="Turn-offs" value={a.turn_off || "—"} wide />
        <ProfileCell label="Green Flags" value={a.green_flags || "—"} wide />
        <ProfileCell
          label="Instagram"
          value={a.social_handle || "—"}
          accent="#00E5FF"
        />
      </section>

      {/* Filters */}
      <div className="border border-white/10 bg-white/[0.02] p-4 mb-6">
        <div className="font-accent text-[11px] tracking-[0.22em] text-white/55 mb-3">
          FILTER CANDIDATES
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Select
            testid="filter-lane"
            label="Lane"
            value={filters.lane}
            onChange={(v) => setFilters((f) => ({ ...f, lane: v }))}
            options={["", ...LANES]}
          />
          <Select
            testid="filter-state"
            label="State"
            value={filters.state}
            onChange={(v) => setFilters((f) => ({ ...f, state: v }))}
            options={["", ...uniqueStates]}
          />
          <Select
            testid="filter-looking"
            label="Looking For"
            value={filters.looking_for}
            onChange={(v) => setFilters((f) => ({ ...f, looking_for: v }))}
            options={["", ...LOOKING_FOR_OPTIONS]}
          />
          <Select
            testid="filter-status"
            label="Status"
            value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            options={["", ...STATUS_OPTIONS_DATING]}
          />
          <Select
            testid="filter-drama"
            label="Peace / Chaos"
            value={filters.drama}
            onChange={(v) => setFilters((f) => ({ ...f, drama: v }))}
            options={["", ...DRAMA_OPTIONS]}
          />
        </div>
      </div>

      {/* Saved matches */}
      {savedMatches.length > 0 && (
        <section
          className="mb-8 border border-[#FF2D9C]/40 bg-[#FF2D9C]/5 p-5"
          data-testid="saved-matches"
        >
          <div className="font-accent text-[11px] tracking-[0.22em] text-[#FF2D9C] mb-3 flex items-center gap-2">
            <BookmarkCheck className="w-3.5 h-3.5" /> SAVED MATCHES (
            {savedMatches.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {savedMatches.map((m) => {
              const c = candidates.find((x) => x.id === m.match_id);
              return (
                <span
                  key={m.id}
                  className="inline-flex items-center gap-2 border border-white/15 bg-black/30 px-3 py-1.5 text-sm"
                >
                  <Heart className="w-3 h-3 text-[#FF2D9C]" />
                  {c?.full_name || m.match_id.slice(0, 8)}
                </span>
              );
            })}
          </div>
        </section>
      )}

      {/* Suggested matches */}
      <section data-testid="suggested-matches">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-black">
            Suggested Matches
            <span className="text-white/40 text-sm font-normal ml-2">
              {scored.length} of {filteredCandidates.length}
            </span>
          </h2>
          <span className="font-accent text-[10px] tracking-[0.22em] text-white/40">
            RANKED BY CHEMISTRY SCORE
          </span>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-white/60 py-12 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Scoring chemistry…
          </div>
        ) : err ? (
          <div className="text-[#FF2D9C] flex items-center gap-2 py-12 justify-center">
            <AlertCircle className="w-4 h-4" /> {err}
          </div>
        ) : scored.length === 0 ? (
          <div className="border border-dashed border-white/10 py-12 text-center text-white/50">
            No compatible candidates found. Try relaxing filters or adding more
            LFL applicants.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {scored.map(({ cand, s }) => (
              <MatchCard
                key={cand.id}
                applicant={applicant}
                cand={cand}
                score={s}
                saved={savedIds.has(cand.id)}
                saving={savingId === cand.id}
                onSave={() => saveMatch(cand, s.reason, s.dynamic)}
              />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function ProfileCell({ label, value, wide, accent }) {
  return (
    <div
      className={`border border-white/10 bg-white/[0.02] p-3 ${wide ? "md:col-span-3" : ""}`}
    >
      <div className="font-accent text-[10px] tracking-widest text-white/45 mb-1">
        {label.toUpperCase()}
      </div>
      <div
        className="text-sm whitespace-pre-wrap break-words"
        style={{ color: accent || "rgba(255,255,255,0.85)" }}
      >
        {value}
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <div className="font-accent text-[10px] tracking-widest text-white/45 mb-1.5">
        {label.toUpperCase()}
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2 text-sm bg-black/40 border border-white/10 outline-none focus:border-[#00E5FF]"
      >
        {options.map((o) => (
          <option key={o || "_any"} value={o}>
            {o || "Any"}
          </option>
        ))}
      </select>
    </label>
  );
}

function MatchCard({ applicant, cand, score, saved, saving, onSave }) {
  const cd = cand.data || {};
  return (
    <div
      className="border border-white/10 bg-white/[0.02] p-5 hover:border-[#FF2D9C]/40 transition-colors"
      data-testid={`match-card-${cand.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="font-heading font-black text-xl text-white truncate">
            {cand.full_name}
          </div>
          <div className="text-xs text-white/55 mt-0.5">
            {cand.city || "—"}, {cand.state || "—"}
            {cd.social_handle ? (
              <>
                {" "}
                ·{" "}
                <span className="text-[#00E5FF]">{cd.social_handle}</span>
              </>
            ) : null}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-heading text-2xl font-black text-[#FF2D9C] leading-none">
            {score.score}
          </div>
          <div className="font-accent text-[9px] tracking-widest text-white/40 mt-0.5">
            CHEM SCORE
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {(cd.lanes || []).map((l) => (
          <span
            key={l}
            className="px-2 py-0.5 text-[10px] font-accent tracking-widest border border-[#00E5FF]/40 text-[#00E5FF] bg-[#00E5FF]/5"
          >
            {l.toUpperCase()}
          </span>
        ))}
        {cd.looking_for && (
          <span className="px-2 py-0.5 text-[10px] font-accent tracking-widest border border-white/15 text-white/70">
            {cd.looking_for.toUpperCase()}
          </span>
        )}
        {cd.drama_level && (
          <span className="px-2 py-0.5 text-[10px] font-accent tracking-widest border border-[#FF2D9C]/40 text-[#FF2D9C] bg-[#FF2D9C]/5">
            {cd.drama_level}
          </span>
        )}
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-start gap-2 text-sm">
          <Zap className="w-3.5 h-3.5 text-[#00E5FF] mt-0.5 shrink-0" />
          <div>
            <div className="font-accent text-[10px] tracking-widest text-white/45 mb-0.5">
              CHEMISTRY REASON
            </div>
            <div className="text-white/85">{score.reason}</div>
          </div>
        </div>
        <div className="flex items-start gap-2 text-sm">
          <Flame className="w-3.5 h-3.5 text-[#FF2D9C] mt-0.5 shrink-0" />
          <div>
            <div className="font-accent text-[10px] tracking-widest text-white/45 mb-0.5">
              ON-CAMERA DYNAMIC
            </div>
            <div className="text-white/85">{score.dynamic}</div>
          </div>
        </div>
        {score.caution && score.caution.length > 0 && (
          <div className="flex items-start gap-2 text-sm">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <div className="font-accent text-[10px] tracking-widest text-white/45 mb-0.5">
                HEADS UP
              </div>
              <div className="text-white/70 text-xs">
                {score.caution.join(" · ")}
              </div>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={onSave}
        disabled={saved || saving}
        data-testid={`save-match-${cand.id}`}
        className={`inline-flex items-center gap-2 px-4 py-2 font-accent text-[11px] tracking-[0.22em] transition-all ${
          saved
            ? "bg-[#FF2D9C]/20 text-[#FF2D9C] border border-[#FF2D9C]/40 cursor-default"
            : "bg-[#FF2D9C] text-white glow-pink hover:scale-[1.03]"
        } ${saving ? "opacity-60" : ""}`}
      >
        {saved ? (
          <>
            <BookmarkCheck className="w-3.5 h-3.5" /> SAVED
          </>
        ) : saving ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> SAVING…
          </>
        ) : (
          <>
            <Bookmark className="w-3.5 h-3.5" /> SAVE MATCH
          </>
        )}
      </button>
    </div>
  );
}
